import React, { useState, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { Link, useRouteMatch, useHistory } from "react-router-dom";
import {
  FaBullhorn,
  FaSignOutAlt,
  FaRegClock,
  FaRegChartBar,
  FaCogs,
  FaGift,
  FaUserSecret,
} from "react-icons/fa";
import { useSpring, animated } from "react-spring";
import { flexbox, FlexboxProps, space, SpaceProps } from "styled-system";
import useWindowSize from "../hooks/useWindowSize";
import { useFeatures } from "../hooks/FeaturesProvider";
import { Flex } from "./Flex";
import { max } from "lodash";
import { IconType } from "react-icons";
import { useUser } from "auth";

type HamburgerContainerProps = {
  active: boolean;
};

const HamburgerContainer = styled.button<{ active: number }>`
  width: 44px;
  height: 37px;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  flex-direction: column;
  background: none;
  border: none;
  position: absolute;
  top: 7px;
  right: 7px;
  z-index: 1000;
  padding: 10px;
  cursor: pointer;

  &:active,
  &:focus {
    outline: 0;
  }

  span {
    height: 3px;
    width: 100%;
    display: block;
    background: ${(props) => props.theme.colors.body};
    transition: all 0.3s ease;

    &:nth-child(1) {
      transform: ${(props) =>
        props.active ? "translateY(7px) rotate(45deg)" : "none"};
    }

    &:nth-child(2) {
      opacity: ${(props) => (props.active ? 0 : 1)};
    }

    &:nth-child(3) {
      transform: ${(props) =>
        props.active ? "translateY(-7px) rotate(-45deg)" : "none"};
    }
  }

  @media only screen and (min-width: ${(props) => props.theme.breakpoints.sm}) {
    display: none;
  }
`;

const Hamburger = ({
  active,
  ...rest
}: HamburgerContainerProps & React.HTMLAttributes<HTMLButtonElement>) => (
  <HamburgerContainer active={active ? 1 : 0} {...rest}>
    <span />
    <span />
    <span />
  </HamburgerContainer>
);

const Logo = styled.img<SpaceProps>`
  height: 30px;
  ${space};
`;

const NavbarWrapper = styled.div`
  height: 50px;
  width: 100%;
`;

const RedDot = styled.div`
  width: 6px;
  height: 6px;
  position: absolute;
  top: 0;
  left: -4px;
  background: ${(props) => props.theme.colors.danger};
  border-radius: 50%;
`;

const NavbarContainer = styled(animated.aside)<FlexboxProps>`
  background: ${(props) => props.theme.colors.background};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px;
  padding-left: 18px;
  padding-right: 18px;
  box-shadow: ${(props) => props.theme.shadows[1]};
  overflow: hidden;
  ${flexbox};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4000;
`;

const NavbarLink = styled(Link)<{ active: any }>`
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  color: ${(props) =>
    props.active ? props.theme.colors.primary : props.theme.colors.body};

  & svg {
    margin-right: 6px;
  }

  &:hover {
    color: ${(props) => props.theme.colors.primary};
    text-decoration: none;
  }

  ${space};
`;

const NavbarAnchor = styled.a<SpaceProps>`
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  color: ${(props) => props.theme.colors.body};

  & svg {
    margin-right: 6px;
  }

  &:hover {
    color: ${(props) => props.theme.colors.primary};
    text-decoration: none;
  }

  ${space};
`;

const NavbarButton = styled.button<SpaceProps>`
  background: none;
  border: 0;
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  color: ${(props) => props.theme.colors[props.color]};

  & svg {
    margin-right: 6px;
  }

  &:hover {
    color: ${(props) => props.theme.colors.primary};
    text-decoration: none;
  }

  &:active,
  &:focus {
    outline: 0;
  }

  ${space};
`;

type NavbarItemsProps = {
  icon: IconType;
  to?: string;
  children: React.ReactNode;
  onClick?: () => void;
} & SpaceProps;

const NavbarItem = ({
  icon: Icon,
  to,
  children,
  ...rest
}: NavbarItemsProps) => {
  const match = useRouteMatch(to);

  return (
    <NavbarLink to={to} active={to != null && match != null ? 1 : 0} {...rest}>
      <Icon />
      {children}
    </NavbarLink>
  );
};

const LASTSEEN_TOKEN = "aika-lastfeature";

export const Navbar = ({ onShowFeatures }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const history = useHistory();
  const windowSize = useWindowSize();
  const features = useFeatures();
  const { user } = useUser();
  const [lastSeen, setLastSeen] = useState<number>(
    parseInt(window.localStorage.getItem(LASTSEEN_TOKEN), 10) || 0
  );

  const newFeature =
    max(features.map((x) => new Date(x.dateAdded).getTime())) > lastSeen;

  const containerProps = useSpring({
    height: (open && windowSize.width < 768 ? windowSize.height : "50") + "px",
  });

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : null;

    return () => {
      document.body.style.overflow = null;
    };
  }, [open]);

  useEffect(() => {
    return history.listen(() => setOpen(false));
  }, [history]);

  const showFeatures = () => {
    const t = new Date().getTime();
    setLastSeen(t);
    window.localStorage.setItem(LASTSEEN_TOKEN, t.toString());
    onShowFeatures();
  };

  return (
    <NavbarWrapper>
      <NavbarContainer
        style={containerProps}
        flexDirection={["column", "row"]}
        alignItems={["flex-start", "center"]}
      >
        <Hamburger onClick={() => setOpen((old) => !old)} active={open} />
        <Flex
          flexDirection={["column", "row"]}
          flex="0 0 auto"
          alignItems={["flex-start", "center"]}
        >
          <Logo src={theme.logo} mr={5} mb={[5, 0]} />
          <NavbarItem
            to="/schedule"
            icon={FaRegClock}
            mr={3}
            mb={[2, 0]}
            onClick={() => setOpen(false)}
          >
            Schedule
          </NavbarItem>
          <NavbarItem to="/report" icon={FaRegChartBar} mr={3} mb={[2, 0]}>
            Insights
          </NavbarItem>
          {user.isAdmin && (
            <NavbarItem to="/admin-dashboard" icon={FaUserSecret}>
              Admin Dashboard
            </NavbarItem>
          )}
        </Flex>
        <Flex flexDirection={["column", "row"]} flex="0 0 auto">
          <NavbarAnchor
            href="https://aika.hellonext.co/"
            mb={[2, 0]}
            mr={3}
            target="_blank"
          >
            <FaBullhorn />
            Feedback
          </NavbarAnchor>
          <NavbarButton
            onClick={showFeatures}
            mb={[2, 0]}
            mr={3}
            color={newFeature ? "primary" : "body"}
          >
            <div style={{ position: "relative" }}>
              <FaGift />
              {newFeature && <RedDot />}
            </div>
            What's New?
          </NavbarButton>
          <NavbarItem to="/settings" icon={FaCogs} mb={[2, 0]} mr={3}>
            Settings
          </NavbarItem>
          <NavbarItem to="/logout" icon={FaSignOutAlt} mb={[2, 0]}>
            Sign Out
          </NavbarItem>
        </Flex>
      </NavbarContainer>
    </NavbarWrapper>
  );
};
